import { Delete, MarkEmailRead } from "@mui/icons-material";
import { Card, IconButton, Stack, Typography } from "@mui/material";
import { Box } from "@mui/system";

import { getNotificationMessage, isNotificationUnread } from "./content";
import {
  EVOTRUX_BLUE,
  UTM_SOURCE_IN_APP_NOTIFICATION,
  UTM_SOURCE_QUERY_PARAM_KEY,
} from "../../../../constants";
import {
  appendQueryParamToUrl,
  ISOStringToLocalReadableTimestamp,
} from "../../../../utils";

interface Props {
  notification: Notification;
  onClose: VoidFunction;
  onMarkNotifictionAsRead: (notification: Notification) => Promise<void>;
  onMarkNotifictionAsDeleted: (notification: Notification) => void;
}

export function NotificationCard(props: Props) {
  const {
    notification,
    onClose,
    onMarkNotifictionAsRead,
    onMarkNotifictionAsDeleted,
  } = props;

  const closeModalAndRedirect = (url: string): void => {
    onClose();
    window.location.href = url;
  };

  const onCardClick = async (): Promise<void> => {
    await onMarkNotifictionAsRead(notification);

    if (notification.url) {
      const urlWithUtmSource = appendQueryParamToUrl(
        notification.url,
        UTM_SOURCE_QUERY_PARAM_KEY,
        UTM_SOURCE_IN_APP_NOTIFICATION,
      );
      closeModalAndRedirect(urlWithUtmSource);
    } else {
      onClose();
    }
  };

  const isUnread = isNotificationUnread(notification);

  return (
    <Card
      variant="outlined"
      sx={{
        "&:hover": {
          bgcolor: "#f8f8f8",
          cursor: "pointer",
        },
        bgcolor: isUnread ? "#ffffff" : "#e6e5e3",
      }}
    >
      <Stack direction="row" p=".3rem">
        <Box
          {...(isUnread
            ? { role: "img", "aria-label": "Unread" }
            : { "aria-hidden": true })}
          sx={{
            alignSelf: "center",
            flexShrink: 0,
            width: "0.5rem",
            height: "0.5rem",
            mx: "0.5rem",
            borderRadius: "50%",
            bgcolor: isUnread ? EVOTRUX_BLUE : "transparent",
          }}
        />
        <Stack direction="column" sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box onClick={onCardClick}>
            <Typography variant="h6">
              {notification?.type
                ? getNotificationMessage(
                    notification.type,
                    notification.metadata,
                  )
                : ""}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {notification?.created_at
                ? ISOStringToLocalReadableTimestamp(notification.created_at)
                : ""}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {!isUnread
                ? `Marked as read on ${ISOStringToLocalReadableTimestamp(notification.read_at)}`
                : ""}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" sx={{ flexShrink: 0 }}>
          <IconButton onClick={() => onMarkNotifictionAsDeleted(notification)}>
            <Delete />
          </IconButton>
          <IconButton
            onClick={async () => onMarkNotifictionAsRead(notification)}
          >
            <MarkEmailRead />
          </IconButton>
        </Stack>
      </Stack>
    </Card>
  );
}
